import { memo, ReactElement, useCallback, useMemo, useRef } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Switch, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { BoxedSwitch, Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, ErcProfile, PenaltyTier } from '@pmp/api';
import { useLinkedToActiveTodaySwitch, useListTableState, useTableView } from '@pmp/hooks';
import { ListProps } from '@pmp/types';
import { useExportTemplatesStore } from '@pmp/stores';

import { ercListColumns, ercProfilesFlatColumns, periodColumns } from '../tableConfig';
import { ErcProfileData } from '../interfaces';
import { Guard, useGuard } from '@ohpen/react-authz';
import { formatDate, lockActionTitle } from '@pmp/utils';
import { useChangeLogAction } from '../../changeLog';

const { Text } = Typography;

const entityType = ActionEntityType.ERC_PROFILES;
const rowKey = 'ercProfileId';

const toChangeLogRecord = (record: ErcProfile) => ({ id: record.ercProfileId ?? '', name: record.name ?? record.ercProfileId ?? '' });

export const ErcProfilesList = memo(
  ({ isFetching, data, valueLists, onCreate, onView, onEdit, onDelete }: ListProps<ErcProfile>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
    const { tableViews, saveTableView, deleteTableView } = useTableView(entityType);
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });
    const tableState = useListTableState<ErcProfile>(entityType, rowKey, tableRef, ercListColumns);
    const { linkedToActiveTodayOnly, onToggleLinkedToActiveToday, onChange, setActiveFilters } = useLinkedToActiveTodaySwitch(
      tableRef,
      tableState,
    );
    const { t } = useTranslation();
    const { changeLogAction, changeLogDrawer } = useChangeLogAction(entityType, toChangeLogRecord);

    const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.ERC_PROFILES), [getExportTemplates]);

    const getRowActions: (record: ErcProfile) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('enso.buttons.view')} placement="top">
            <Button type="text" aria-label="View ERC" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['ercProfiles:update']} flags={['enableErcProfilesCrud']}>
            <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit ERC"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['ercProfiles:delete']} flags={['enableErcProfilesCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete ERC"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          changeLogAction(record),
        ];

        return rowActions;
      },
      [onDelete, onEdit, onView, t, changeLogAction],
    );

    const tableProps: TableProps<ActionEntityType, ErcProfile, PenaltyTier, unknown, ErcProfileData> = useMemo(
      () => ({
        ...tableState,
        id: entityType,
        itemsLabel: t('pmp.erc.labels.ercProfile'),
        ref: tableRef,
        dataSource: data ?? [],
        columns: ercListColumns,
        exportColumns: ercProfilesFlatColumns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        resizableColumns: true,
        loading: isFetching,
        getRowActions,
        expanded: {
          columns: periodColumns,
          columnsKey: 'penaltyTiers',
          rowKey: 'order',
        },
        showRowSelection,
        showViews: true,
        tableViews,
        saveTableView,
        deleteTableView,
        valueLists,
        onChange,
        setActiveFilters,
      }),
      [
        tableState,
        t,
        data,
        isFetching,
        getRowActions,
        showRowSelection,
        tableViews,
        saveTableView,
        deleteTableView,
        valueLists,
        onChange,
        setActiveFilters,
      ],
    );

    return (
      <>
        <Table {...tableProps}>
          <>
            <Table.ActionsBar>
              <Flex justify="space-between">
                <Space size="middle">
                  <Table.GlobalSearch />
                  <BoxedSwitch>
                    <Text>{t('pmp.erc.labels.linkedToActiveToday')}</Text>
                    <Switch
                      checked={linkedToActiveTodayOnly}
                      onChange={onToggleLinkedToActiveToday}
                      aria-label={t('pmp.erc.labels.linkedToActiveToday')}
                    />
                  </BoxedSwitch>
                </Space>
                <Space size="middle">
                  <Table.ResetFiltersButton />
                  <Table.SelectColumnsButton />
                  <Guard flags={['enableExports']}>
                    <Table.ExportButton
                      editableFileName
                      fileName={`ERC profiles ${formatDate()}`}
                      sheetName="ERC profiles"
                      disabled={isFetching}
                      loadingTemplates={isLoadingExportTemplates}
                      getTemplates={getTemplates}
                      modalDefaults={{ columnScope: 'template' }}
                      allowSearch={true}
                      exportView
                      flatten
                    />
                  </Guard>
                  <Guard permissions={['ercProfiles:create']} flags={['enableErcProfilesCrud']}>
                    <Table.CreateButton onClick={onCreate} />
                  </Guard>
                </Space>
              </Flex>
            </Table.ActionsBar>
            <Table.BulkBar xItemsSelectedLabel="pmp.erc.labels.xErcProfilesSelected">
              <Space size="middle">
                <Guard flags={['enableExports']}>
                  <Table.ExportButton
                    editableFileName
                    fileName={`ERC profiles ${formatDate()}`}
                    sheetName="ERC profiles"
                    disabled={isFetching}
                    loadingTemplates={isLoadingExportTemplates}
                    allowSearch={true}
                    getTemplates={getTemplates}
                    modalDefaults={{ columnScope: 'template' }}
                    exportView
                    flatten
                  />
                </Guard>
              </Space>
            </Table.BulkBar>
          </>
        </Table>
        {changeLogDrawer}
      </>
    );
  },
);
